import React from 'react';
import styles from './styles/LocalesPage.module.css';
import { FaClock, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaPhone, FaVideo, FaExternalLinkAlt } from 'react-icons/fa';
import StructuredData from '../components/StructuredData';
import FaqAnswer from '../components/FaqAnswer';
import { companyFaqs, toFaqPageJsonLd } from '../data/faqData';
import { BRAND } from '../data/brand';

interface LocalData {
  id: number;
  nombre: string;
  barrio: string;
  direccion: string;
  horario: string;
  mapaUrl: string;
  mapsLink: string;
  whatsapp?: string;
  whatsappDisplay?: string;
  telefono?: string;
  telefonoDisplay?: string;
  email: string;
  tiendaVirtual: {
    nombre: string;
    horario: string;
    zonaHoraria: string;
    meetUrl: string;
    phoneLabel: string;
    pin: string;
  };
}

const locales: LocalData[] = [
  {
    id: 1,
    nombre: 'Barrio Martin',
    barrio: '3 de Febrero',
    direccion: '3 de Febrero 380, Rosario',
    horario: 'Lunes a sabados de 9 a 21 hrs',
    whatsapp: '3413080099',
    whatsappDisplay: '341 308-0099',
    telefono: '3417690034',
    telefonoDisplay: '341 769-0034',
    email: 'pedidos@garciavidal.com',
    mapaUrl: 'https://www.google.com/maps?q=3%20de%20Febrero%20380%2C%20Rosario%2C%20Santa%20Fe%2C%20Argentina&output=embed',
    mapsLink: 'https://www.google.com/maps/search/?api=1&query=3%20de%20Febrero%20380%2C%20Rosario%2C%20Santa%20Fe%2C%20Argentina',
    tiendaVirtual: {
      nombre: 'Tienda Virtual 3 de Febrero',
      horario: 'Miercoles 1 julio, 9:00 a 21:00',
      zonaHoraria: 'America/Argentina/Cordoba',
      meetUrl: 'https://meet.google.com/nwg-xrca-xie',
      phoneLabel: '+54 11 3986-3700',
      pin: '256 812 301 3511'
    }
  },
  {
    id: 2,
    nombre: 'Sarmiento',
    barrio: 'Centro',
    direccion: 'Sarmiento 1183, Rosario',
    horario: 'Lunes a sabados de 9 a 21 hrs',
    whatsapp: '3416574963',
    whatsappDisplay: '+54 9 3416 57-4963',
    telefono: '3444621289',
    telefonoDisplay: '+54 9 3444 62-1289',
    email: 'pedidos@garciavidal.com',
    mapaUrl: 'https://www.google.com/maps?q=Sarmiento%201183%2C%20Rosario%2C%20Santa%20Fe%2C%20Argentina&output=embed',
    mapsLink: 'https://www.google.com/maps/search/?api=1&query=Sarmiento%201183%2C%20Rosario%2C%20Santa%20Fe%2C%20Argentina',
    tiendaVirtual: {
      nombre: 'Tienda Virtual Sarmiento',
      horario: 'Miercoles 1 julio, 9:00 a 21:00',
      zonaHoraria: 'America/Argentina/Cordoba',
      meetUrl: 'https://meet.google.com/mvr-cohb-qmo',
      phoneLabel: '+54 11 3986-3700',
      pin: '423 048 268 6553'
    }
  }
];

const localBusinessSchema = locales.map((local) => ({
  '@context': 'https://schema.org',
  '@type': 'Store',
  name: `${BRAND.name} - ${local.nombre}`,
  address: {
    '@type': 'PostalAddress',
    streetAddress: local.direccion.replace(', Rosario', ''),
    addressLocality: 'Rosario',
    addressRegion: 'Santa Fe',
    addressCountry: 'AR'
  },
  url: 'https://familiagarciavidal.com/locales',
  hasMap: local.mapsLink,
  telephone: local.telefono ? `+54 9 ${local.telefono}` : undefined,
  email: local.email,
  areaServed: 'Rosario'
}));

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: BRAND.name,
  url: 'https://familiagarciavidal.com',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      areaServed: 'AR',
      availableLanguage: 'Spanish'
    }
  ],
  location: localBusinessSchema
};

const LocalesPage: React.FC = () => {
  return (
    <div className={styles['locales-container']}>
      <StructuredData data={[organizationSchema, ...localBusinessSchema, toFaqPageJsonLd(companyFaqs)]} />

      <header className={styles['locales-header']}>
        <span>Locales y atencion online</span>
        <h1>Nuestros Locales</h1>
        <p>Veni a conocer la calidad de siempre, cerca de tu casa o desde una tienda virtual.</p>
      </header>

      <div className={styles['locales-list']}>
        {locales.map((local) => (
          <article key={local.id} className={styles['local-card']}>
            <div className={styles['local-info']}>
              <span className={styles['local-kicker']}>{local.barrio}</span>
              <h2>{local.nombre}</h2>

              <div className={`${styles['info-item']} ${styles.address}`}>
                <span className={styles.icon}><FaMapMarkerAlt /></span>
                <p>{local.direccion}</p>
              </div>

              <div className={`${styles['info-item']} ${styles.hours}`}>
                <span className={styles.icon}><FaClock /></span>
                <p>{local.horario}</p>
              </div>

              <div className={styles['virtual-store']}>
                <div>
                  <span className={styles['virtual-label']}>Tienda virtual</span>
                  <h3>{local.tiendaVirtual.nombre}</h3>
                  <p>{local.tiendaVirtual.horario}</p>
                  <small>{local.tiendaVirtual.zonaHoraria} | Tel. {local.tiendaVirtual.phoneLabel} | PIN {local.tiendaVirtual.pin}</small>
                </div>
                <a
                  href={local.tiendaVirtual.meetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles['btn-virtual']}
                >
                  <FaVideo /> Entrar por Meet
                </a>
              </div>

              <div className={styles['contact-buttons']}>
                {local.whatsapp && local.whatsappDisplay && (
                  <a
                    href={`https://wa.me/549${local.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles['btn-contact']} ${styles['btn-whatsapp']}`}
                  >
                    <FaWhatsapp /> WhatsApp: {local.whatsappDisplay}
                  </a>
                )}

                {local.telefono && local.telefonoDisplay && (
                  <a
                    href={`tel:+549${local.telefono}`}
                    className={`${styles['btn-contact']} ${styles['btn-phone']}`}
                  >
                    <FaPhone /> Llamadas: {local.telefonoDisplay}
                  </a>
                )}

                <a
                  href={`mailto:${local.email}`}
                  className={`${styles['btn-contact']} ${styles['btn-email']}`}
                >
                  <FaEnvelope /> {local.email}
                </a>

                <a
                  href={local.mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles['btn-contact']} ${styles['btn-map']}`}
                >
                  <FaExternalLinkAlt /> Abrir mapa
                </a>
              </div>
            </div>

            <div className={styles['local-map']}>
              <iframe
                src={local.mapaUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`Mapa ${local.nombre}`}
              />
            </div>
          </article>
        ))}
      </div>

      <section className={styles['company-faq']} aria-labelledby="company-faq-title">
        <div className={styles['company-faq-header']}>
          <span>FAQ empresa</span>
          <h2 id="company-faq-title">Preguntas frecuentes de {BRAND.name}</h2>
        </div>
        <div className={styles['company-faq-list']}>
          {companyFaqs.map((faq) => (
            <article className={styles['company-faq-item']} key={faq.question}>
              <h3>{faq.question}</h3>
              <FaqAnswer answer={faq.answer} className={styles['company-faq-answer']} />
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default LocalesPage;